// src/pages/sales/QuotationDetailModal.jsx
import { useState, useEffect } from "react";
import { downloadPdf, fetchQuotationRevisions } from "../../apiService";
import { fmtDate } from "../../utils/dateUtils";

const STATUS_OPTIONS = ["DRAFT", "SENT", "ACCEPTED", "REJECTED", "EXPIRED"];

function RevisionChange({ change }) {
  const s = { fontSize: 11, display: "flex", gap: 6, alignItems: "baseline", marginBottom: 2 };
  if (change.kind === "field") return (
    <div style={s}>
      <span style={{ color: "#6b7280" }}>{change.label}:</span>
      <span style={{ textDecoration: "line-through", color: "#dc2626" }}>{change.from || "—"}</span>
      <span style={{ color: "#6b7280" }}>→</span>
      <span style={{ color: "#059669" }}>{change.to || "—"}</span>
    </div>
  );
  if (change.kind === "line_added") return (
    <div style={s}>
      <span style={{ color: "#059669", fontWeight: 600 }}>+ Added:</span>
      <span>{change.product}</span>
    </div>
  );
  if (change.kind === "line_removed") return (
    <div style={s}>
      <span style={{ color: "#dc2626", fontWeight: 600 }}>− Removed:</span>
      <span style={{ textDecoration: "line-through", color: "#6b7280" }}>{change.product}</span>
    </div>
  );
  if (change.kind === "line_qty") return (
    <div style={s}>
      <span style={{ color: "#6b7280" }}>{change.product} — Qty:</span>
      <span style={{ textDecoration: "line-through", color: "#dc2626" }}>{change.from}</span>
      <span style={{ color: "#6b7280" }}>→</span>
      <span style={{ color: "#059669" }}>{change.to}</span>
    </div>
  );
  if (change.kind === "line_price") return (
    <div style={s}>
      <span style={{ color: "#6b7280" }}>{change.product} — Price:</span>
      <span style={{ textDecoration: "line-through", color: "#dc2626" }}>{change.from}</span>
      <span style={{ color: "#6b7280" }}>→</span>
      <span style={{ color: "#059669" }}>{change.to}</span>
    </div>
  );
  return null;
}

const INCOTERM_LABELS = {
  EXW: "EXW — Ex-Warehouse",
  DAP: "DAP — Delivered at Place",
  DDP: "DDP — Delivered Duty Paid",
  FOB: "FOB — Free on Board",
  CFR: "CFR — Cost and Freight",
  CIF: "CIF — Cost, Insurance & Freight",
};

function fmt(n) {
  return Number(n).toLocaleString("en-AE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function QuotationDetailModal({ quotation: q, canCreate, onStatusChange, onEdit, onClose }) {
  const [statusChanging, setStatusChanging] = useState(false);
  const [revisions,      setRevisions]      = useState(null);
  const [revOpen,        setRevOpen]        = useState(false);

  useEffect(() => {
    fetchQuotationRevisions(q.id)
      .then(setRevisions)
      .catch(() => setRevisions([]));
  }, [q.id]);

  const lines      = q.lines || [];
  const subtotal   = lines.reduce((s, l) => s + Number(l.quantity) * Number(l.unitPrice), 0);
  const vatAmount  = q.vatEnabled ? subtotal * 0.05 : 0;
  const grandTotal = subtotal + vatAmount;
  const companyLabel = q.quoNumber?.startsWith("AMIQ") ? "AMI — Industries" : "AMT — Trading";
  const currency   = q.currency || "AED";

  async function handleStatus(status) {
    setStatusChanging(true);
    try { await onStatusChange(q.id, status); }
    finally { setStatusChanging(false); }
  }

  return (
    <div className="modal" style={{ width: 700, maxHeight: "90vh", overflowY: "auto" }}>
      <div className="modal-header">
        <div className="modal-title" style={{ fontFamily: "monospace" }}>{q.quoNumber}</div>
        <button className="modal-close" onClick={onClose}>✕</button>
      </div>
      <div className="modal-body">

        {/* Header row */}
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: "#888" }}>Customer</div>
            <div style={{ fontWeight: 600 }}>{q.customerName}</div>
            {q.shipToName    && <div style={{ fontSize: 12, color: "#555" }}>{q.shipToName}</div>}
            {q.addressedTo   && <div style={{ fontSize: 12 }}>Kind Attn: {q.addressedTo}</div>}
            {q.contactPerson && <div style={{ fontSize: 12 }}>Contact: {q.contactPerson}</div>}
            {q.reference     && <div style={{ fontSize: 12, color: "#888" }}>Ref: {q.reference}</div>}
          </div>
          <div style={{ minWidth: 160 }}>
            <div style={{ fontSize: 11, color: "#888" }}>Date</div>
            <div style={{ fontWeight: 600 }}>{q.date ? fmtDate(q.date) : "—"}</div>
            <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>Company</div>
            <div style={{ fontSize: 12 }}>{companyLabel}</div>
            <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>Prepared By</div>
            <div style={{ fontSize: 12 }}>{q.preparedBy}</div>
            <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>Validity</div>
            <div style={{ fontSize: 12 }}>{q.validityDays} days</div>
          </div>
        </div>

        {/* Line items */}
        <table className="data-table" style={{ marginBottom: 8 }}>
          <thead>
            <tr>
              <th style={{ width: 32 }}>No.</th>
              <th>Description</th>
              <th style={{ textAlign: "right", width: 70 }}>Qty</th>
              <th style={{ width: 50 }}>UOM</th>
              <th style={{ width: 90 }}>Packing</th>
              <th style={{ textAlign: "right", width: 90 }}>Price ({currency})</th>
              <th style={{ textAlign: "right", width: 100 }}>Amount ({currency})</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={l.id}>
                <td style={{ textAlign: "center" }}>{i + 1}</td>
                <td>
                  <span style={{ fontWeight: 600 }}>{l.productName}</span>
                  {l.productCode && <span style={{ fontSize: 11, color: "#888" }}> ({l.productCode})</span>}
                </td>
                <td style={{ textAlign: "right" }}>{Number(l.quantity).toFixed(3)}</td>
                <td>{l.uom}</td>
                <td style={{ fontSize: 11, color: "#6b7280" }}>{l.packingSize || "—"}</td>
                <td style={{ textAlign: "right" }}>{Number(l.unitPrice).toFixed(4)}</td>
                <td style={{ textAlign: "right", fontWeight: 600 }}>
                  {fmt(Number(l.quantity) * Number(l.unitPrice))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals */}
        <div style={{ textAlign: "right", fontSize: 12, paddingRight: 4, marginBottom: 12 }}>
          <div>Sub Total: <strong>{currency} {fmt(subtotal)}</strong></div>
          {q.vatEnabled  && <div>VAT 5%: <strong>{currency} {fmt(vatAmount)}</strong></div>}
          {!q.vatEnabled && <div style={{ color: "#888", fontSize: 11 }}>*VAT if applicable</div>}
          <div style={{ fontSize: 15, marginTop: 4 }}>Total: <strong>{currency} {fmt(grandTotal)}</strong></div>
        </div>

        {/* Terms */}
        {(q.paymentTerms || q.incoterm || q.notes) && (
          <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb",
            borderRadius: 6, padding: "8px 12px", fontSize: 12, marginBottom: 12 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Terms &amp; Conditions</div>
            {q.paymentTerms && <div><span style={{ fontWeight: 600 }}>Payment: </span>{q.paymentTerms}</div>}
            {q.incoterm     && <div><span style={{ fontWeight: 600 }}>Delivery: </span>{INCOTERM_LABELS[q.incoterm] || q.incoterm}</div>}
            <div><span style={{ fontWeight: 600 }}>Validity: </span>{q.validityDays} days</div>
            {q.notes        && <div style={{ marginTop: 4 }}><span style={{ fontWeight: 600 }}>Note: </span>{q.notes}</div>}
          </div>
        )}

        {/* Status */}
        {canCreate && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>Update Status</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {STATUS_OPTIONS.map(s => (
                <button key={s}
                  className={q.status === s ? "btn btn-primary" : "btn"}
                  style={{ fontSize: 11 }}
                  disabled={statusChanging || q.status === s}
                  onClick={() => handleStatus(s)}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Revision History */}
        {revisions !== null && (
          <div style={{ marginBottom: 12 }}>
            <button
              style={{ background: "none", border: "none", cursor: "pointer",
                fontSize: 12, fontWeight: 700, color: "#440099", padding: 0,
                display: "flex", alignItems: "center", gap: 4 }}
              onClick={() => setRevOpen(o => !o)}>
              {revOpen ? "▾" : "▸"} Revision History
              {revisions.length > 0 && (
                <span style={{ background: "rgba(68,0,153,0.1)", color: "#440099",
                  borderRadius: 10, padding: "1px 7px", fontSize: 11 }}>
                  {revisions.length}
                </span>
              )}
            </button>
            {revOpen && (
              <div style={{ marginTop: 8 }}>
                {revisions.length === 0 ? (
                  <div style={{ fontSize: 12, color: "#aaa" }}>No revisions recorded.</div>
                ) : revisions.map(rev => (
                  <div key={rev.revisionNo} style={{
                    border: "1px solid rgba(68,0,153,0.1)", borderRadius: 8,
                    padding: "8px 12px", marginBottom: 8,
                    background: "rgba(237,232,255,0.35)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between",
                      fontSize: 11, fontWeight: 700, color: "#440099", marginBottom: 4 }}>
                      <span>Revision {rev.revisionNo}</span>
                      <span style={{ fontWeight: 400, color: "#6b7280" }}>
                        {rev.changedAt ? rev.changedAt.replace("T", " ").slice(0, 16) : ""} · {rev.changedBy}
                      </span>
                    </div>
                    {(rev.changes || []).length === 0 ? (
                      <div style={{ fontSize: 12, color: "#aaa" }}>No tracked changes.</div>
                    ) : (rev.changes || []).map((c, i) => (
                      <RevisionChange key={i} change={c} />
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="form-footer" style={{ justifyContent: "space-between" }}>
          <div>
            {canCreate && (q.status === "DRAFT" || q.status === "SENT") && (
              <button className="btn" onClick={() => onEdit(q)}>Edit</button>
            )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn" onClick={onClose}>Close</button>
            <button className="btn btn-primary"
              onClick={() => downloadPdf(
                `/api/crm/quotations/${q.id}/pdf`,
                q.quoNumber.replace(/\//g, "-") + ".pdf"
              )}>Download PDF</button>
          </div>
        </div>

      </div>
    </div>
  );
}
